import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { Button, ButtonGroup, Select } from "north.js";
import React, { useEffect, useMemo } from "react";
import styled from "styled-components";

import { useDeviceSize } from "src/shared/hooks";
import globalConstants from "src/theme/globalConstants";

const DEFAULT_ITEMS_PER_PAGE_OPTIONS = [10, 20, 30, 40, 50];
const DESKTOP_MAX_VISIBLE_PAGE_TOKENS = 12;
const MOBILE_MAX_VISIBLE_PAGE_TOKENS = 5;

type PaginationToken =
  | {
      type: "page";
      page: number;
    }
  | {
      type: "ellipsis";
      key: string;
    };

const getPaginationTokens = (
  totalPages: number,
  currentPage: number,
  maxVisiblePageTokens: number
): PaginationToken[] => {
  if (totalPages <= 0) {
    return [];
  }

  const buildTokensFromPages = (pages: number[]): PaginationToken[] => {
    const sortedPages = Array.from(new Set(pages)).sort(
      (left, right) => left - right
    );

    return sortedPages.reduce<PaginationToken[]>((tokens, page, index) => {
      if (index === 0) {
        tokens.push({ type: "page", page });
        return tokens;
      }

      const previousPage = sortedPages[index - 1];
      const gap = page - previousPage;

      if (gap === 2) {
        tokens.push({ type: "page", page: previousPage + 1 });
      } else if (gap > 2) {
        tokens.push({
          type: "ellipsis",
          key: `ellipsis-${previousPage}-${page}`,
        });
      }

      tokens.push({ type: "page", page });
      return tokens;
    }, []);
  };

  const getPageRange = (start: number, end: number): number[] =>
    Array.from({ length: end - start + 1 }, (_, index) => start + index);

  if (totalPages <= maxVisiblePageTokens) {
    return buildTokensFromPages(getPageRange(0, totalPages - 1));
  }

  const lastPage = totalPages - 1;
  const edgeWindowSize = maxVisiblePageTokens - 2;
  const middleWindowSize = maxVisiblePageTokens - 4;
  const middleWindowStart =
    currentPage - Math.floor((middleWindowSize - 1) / 2);
  const middleWindowEnd = middleWindowStart + middleWindowSize - 1;

  if (middleWindowStart <= 1) {
    return buildTokensFromPages([
      ...getPageRange(0, edgeWindowSize - 1),
      lastPage,
    ]);
  }

  if (middleWindowEnd >= lastPage - 1) {
    return buildTokensFromPages([
      0,
      ...getPageRange(lastPage - edgeWindowSize + 1, lastPage),
    ]);
  }

  return buildTokensFromPages([
    0,
    ...getPageRange(middleWindowStart, middleWindowEnd),
    lastPage,
  ]);
};

export type PageBarProps = {
  page: number;
  itemsPerPage: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (itemsPerPage: number) => void;
  itemsPerPageOptions?: number[];
  label?: React.ReactNode;
  disabled?: boolean;
};

const PageBar: React.FC<PageBarProps> = ({
  page,
  itemsPerPage,
  totalItems,
  onPageChange,
  onItemsPerPageChange,
  itemsPerPageOptions = DEFAULT_ITEMS_PER_PAGE_OPTIONS,
  label = "Items per page:",
  disabled = false,
}) => {
  const isTabletOrSmaller = useDeviceSize("tablet");
  const maxVisiblePageTokens = isTabletOrSmaller
    ? MOBILE_MAX_VISIBLE_PAGE_TOKENS
    : DESKTOP_MAX_VISIBLE_PAGE_TOKENS;

  const totalPages = useMemo(() => {
    if (itemsPerPage <= 0) {
      return 0;
    }

    return Math.ceil(totalItems / itemsPerPage);
  }, [itemsPerPage, totalItems]);

  const lastPage = Math.max(totalPages - 1, 0);
  const paginationTokens = useMemo(
    () => getPaginationTokens(totalPages, page, maxVisiblePageTokens),
    [maxVisiblePageTokens, page, totalPages]
  );

  useEffect(() => {
    if (page > lastPage) {
      onPageChange(lastPage);
      return;
    }

    if (page < 0) {
      onPageChange(0);
    }
  }, [lastPage, onPageChange, page]);

  const isPreviousDisabled = disabled || page <= 0 || totalPages === 0;
  const isNextDisabled = disabled || page >= lastPage || totalPages === 0;

  const handleItemsPerPageChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const nextItemsPerPage = Number(event.target.value);
    onItemsPerPageChange(nextItemsPerPage);

    if (page !== 0) {
      onPageChange(0);
    }
  };

  const renderedLabel =
    typeof label === "string" || typeof label === "number" ? (
      <LabelText>{label}</LabelText>
    ) : (
      label
    );

  return (
    <Container>
      <ItemsPerPageContainer>
        {renderedLabel}
        <Select
          size="sm"
          value={itemsPerPage}
          onChange={handleItemsPerPageChange}
          disabled={disabled}
        >
          {itemsPerPageOptions.map((option) => (
            <Select.Option key={option} value={option}>
              {option}
            </Select.Option>
          ))}
        </Select>
      </ItemsPerPageContainer>
      <ButtonGroup>
        <Button
          size="sm"
          disabled={isPreviousDisabled}
          onClick={() => onPageChange(Math.max(0, page - 1))}
        >
          <ChevronLeftIcon className="w-4 h-4" />
        </Button>
        {paginationTokens.map((token) =>
          token.type === "page" ? (
            <Button
              key={`page-bar-page-${token.page}`}
              size="sm"
              color={page === token.page ? "primary" : "secondary"}
              disabled={disabled}
              onClick={() => onPageChange(token.page)}
            >
              {token.page + 1}
            </Button>
          ) : (
            <Button key={token.key} size="sm" color="secondary" disabled>
              ...
            </Button>
          )
        )}
        <Button
          size="sm"
          disabled={isNextDisabled}
          onClick={() => onPageChange(Math.min(lastPage, page + 1))}
        >
          <ChevronRightIcon className="w-4 h-4" />
        </Button>
      </ButtonGroup>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 16px;
`;

const ItemsPerPageContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const LabelText = styled.span`
  color: ${globalConstants.color?.textSecondary};
`;

export default PageBar;
